<!--
  ~ Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
  ~ Venelin Iliev <http://veneliniliev.com>
  -->

<div class="container-fluid" *ngIf="accessService.hasAccess([], ['invoices.menu'])">
  <div class="d-flex justify-content-between align-items-center">
    <h5 class="title-secondary">
      {{ 'INVOICES.INVOICES_LIST.TITLE' | translate }}
    </h5>

    <div>
      <button type="button" class="btn btn-link btn-clear-filters" (click)="clearFilters()">
        {{ 'CLEAR_FILTERS' | translate }}
      </button>

      <button
        *ngIf="accessService.hasAccess([UserRoleType.FINANCE_AGENT, UserRoleType.FINANCE_MANAGER], ['invoices.store'])"
        type="button"
        class="btn btn-secondary btn-input"
        (click)="add()"
      >
        {{ 'INVOICES.INVOICES_LIST.ADD' | translate }}
      </button>
    </div>
  </div>

  <kendo-grid
    [data]="data"
    [pageSize]="invoicesResponse?.meta.per_page"
    [sortable]="true"
    [filterable]="true"
    [columnMenu]="true"
    [filter]="state?.filter"
    [sort]="state?.sort"
    [skip]="state?.skip"
    [pageable]="true"
    (dataStateChange)="loadData($event)"
    (columnVisibilityChange)="onVisibilityChange($event)"
    [rowClass]="invoiceRowCallback"
    #grid
    (excelExport)="onExcelExport($event, 'Invoices', grid)"
  >
    <kendo-grid-column
      [width]="70"
      class="text-center"
      headerClass="text-center"
      title="{{ 'INVOICES.INVOICES_LIST.NUMBER' | translate }}"
    >
      <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
        {{ rowIndex + 1 }}
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-column
      field="id"
      title="{{ 'INVOICES.INVOICES_LIST.ID' | translate }}"
      filter="numeric"
      [width]="120"
      [hidden]="isHiddenColumn('id', true)"
    ></kendo-grid-column>

    <kendo-grid-column
      field="company.abbreviation"
      title="{{ 'INVOICES.INVOICES_LIST.COMPANY' | translate }}"
      filter="text"
      [hidden]="isHiddenColumn('company.abbreviation')"
    >
      <ng-template kendoGridFilterCellTemplate let-filter>
        <app-kendo-company-select [filter]="filter"></app-kendo-company-select>
      </ng-template>
      <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
        {{ dataItem.company.shortName }}
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-column
      [filterable]="false"
      [sortable]="false"
      field="clientable.name"
      title="{{ 'INVOICES.INVOICES_LIST.CLIENTABLE_NAME' | translate }}"
      [hidden]="isHiddenColumn('clientable.name')"
    >
      <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
        {{ dataItem.clientable?.name }} ({{ dataItem.cleanClientableType }})
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-column
      [filterable]="false"
      [sortable]="false"
      field="cleanInvoiceableType"
      title="{{ 'INVOICES.INVOICES_LIST.INVOICEABLE_TYPE' | translate }}"
      [hidden]="isHiddenColumn('cleanInvoiceableType')"
    >
    </kendo-grid-column>

    <kendo-grid-column
      field="invoiceable_id"
      title="{{ 'INVOICES.INVOICES_LIST.INVOICEABLE_ID' | translate }}"
      [hidden]="isHiddenColumn('invoiceable_id')"
    ></kendo-grid-column>

    <kendo-grid-column
      field="number"
      title="{{ 'INVOICES.INVOICES_LIST.INVOICE_NUMBER' | translate }}"
      filter="numeric"
      [hidden]="isHiddenColumn('number')"
    >
      <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
        {{ dataItem.formatted_number }}
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-column
      field="date"
      title="{{ 'INVOICES.INVOICES_LIST.DATE' | translate }}"
      filter="date"
      format="{0:dd/MM/yyyy}"
      [hidden]="isHiddenColumn('date')"
    >
      <ng-template kendoGridFilterCellTemplate let-filter let-column="column">
        <kendo-grid-date-filter-cell [column]="column" [filter]="filter" operator="eq"></kendo-grid-date-filter-cell>
      </ng-template>

      <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
        {{ dataItem.date | date : env.pipeDateFormat }}
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-column
      field="due_date"
      title="{{ 'INVOICES.INVOICES_LIST.DUE_DATE' | translate }}"
      filter="date"
      format="{0:dd/MM/yyyy}"
      [hidden]="isHiddenColumn('due_date')"
    >
      <ng-template kendoGridFilterCellTemplate let-filter let-column="column">
        <kendo-grid-date-filter-cell [column]="column" [filter]="filter" operator="eq"></kendo-grid-date-filter-cell>
      </ng-template>

      <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
        {{ dataItem.due_date | date : env.pipeDateFormat }}
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-column
      field="type.value"
      title="{{ 'INVOICES.INVOICES_LIST.TYPE' | translate }}"
      [hidden]="isHiddenColumn('type.value')"
    >
      <ng-template kendoGridFilterCellTemplate let-filter>
        <app-kendo-type-select [groupType]="'invoice'" [filter]="filter"></app-kendo-type-select>
      </ng-template>
      <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
        <app-type-badge [type]="dataItem.type"></app-type-badge>
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-column
      field="payment_method.value"
      title="{{ 'INVOICES.INVOICES_LIST.PAYMENT_METHOD' | translate }}"
      [hidden]="isHiddenColumn('payment_method.value')"
    >
      <ng-template kendoGridFilterCellTemplate let-filter>
        <app-kendo-type-select [groupType]="'payment_method'" [filter]="filter" [field]="'payment_method_id'">
        </app-kendo-type-select>
      </ng-template>
      <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
        <app-type-badge [type]="dataItem.payment_method"></app-type-badge>
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-column
      field="total_amount"
      filter="numeric"
      title="{{ 'INVOICES.INVOICES_LIST.TOTAL_AMOUNT' | translate }}"
    >
      <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
        {{ dataItem.total_amount | number : '1.2-2' }} {{ 'DEFAULT_CURRENCY' | translate }}
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-column field="balance" filter="numeric" title="{{ 'INVOICES.INVOICES_LIST.BALANCE' | translate }}">
      <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
        <span
          class="fw-bold"
          [ngClass]="{ 'theme-color-danger': dataItem.balance < 0, 'theme-color-success': dataItem.balance >= 0 }"
        >
          {{ dataItem.balance | number : '1.2-2' }} {{ 'DEFAULT_CURRENCY' | translate }}
        </span>
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-column
      [width]="110"
      field="deposit_percent"
      title="{{ 'INVOICES.INVOICES_LIST.DEPOSIT_PERCENT' | translate }}"
      [hidden]="isHiddenColumn('deposit_percent')"
      filter="numeric"
    ></kendo-grid-column>

    <kendo-grid-column
      field="paid"
      title="{{ 'INVOICES.INVOICES_LIST.PAID' | translate }}"
      filter="boolean"
      [class]="'text-center'"
      [hidden]="isHiddenColumn('paid')"
    >
      <ng-template kendoGridCellTemplate let-dataItem>
        <app-active-indicator [active]="dataItem.paid"></app-active-indicator>
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-column
      title="{{ 'INVOICES.INVOICES_LIST.ACTIONS' | translate }}"
      [width]="100"
      [class]="'text-center'"
      [includeInChooser]="false"
    >
      <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
        <app-edit-button
          *ngIf="accessService.hasAccess([UserRoleType.FINANCE_AGENT, UserRoleType.FINANCE_MANAGER], ['invoices.show'])"
          (click)="edit(dataItem)"
        >
        </app-edit-button>

        <app-download-invoice-button
          *ngIf="
            accessService.hasAccess(
              [
                UserRoleType.RESERVATION_AGENT,
                UserRoleType.RESERVATION_MANAGER,
                UserRoleType.SALES_AGENT,
                UserRoleType.SALES_MANAGER,
                UserRoleType.CUSTOMER_CARE_MANAGER,
                UserRoleType.FINANCE_AGENT,
                UserRoleType.FINANCE_MANAGER
              ],
              ['invoices.download']
            )
          "
          [invoice]="dataItem"
        >
        </app-download-invoice-button>

        <app-payment-button
          [paymentable_id]="dataItem.id"
          [paymentable_type]="LaravelModelsEnum.INVOICE"
          (close)="loadData(state)"
          *ngIf="
            dataItem.type_id != InvoiceType.PROFORMA &&
            accessService.hasAccess([UserRoleType.FINANCE_AGENT, UserRoleType.FINANCE_MANAGER], ['payments.store'])
          "
        >
        </app-payment-button>

        <a
          *ngIf="
            accessService.hasAccess([UserRoleType.FINANCE_AGENT, UserRoleType.FINANCE_MANAGER], ['payments.index'])
          "
          [ngbTooltip]="'INVOICES.INVOICES_LIST.VIEW_PAYMENTS' | translate"
          container="body"
          class="btn btn-warning btn-ico"
          [routerLink]="['/payments']"
          [queryParams]="{ paymentable_id: dataItem.id }"
        >
          <i class="fal fa-file-invoice-dollar"></i>
        </a>

        <button
          *ngIf="
            accessService.hasAccess([UserRoleType.FINANCE_AGENT, UserRoleType.FINANCE_MANAGER], ['invoices.destroy'])
          "
          class="btn btn-danger btn-ico"
          type="button"
          [ngbTooltip]="'INVOICES.INVOICES_LIST.CREATE_CANCELLATION' | translate"
          container="body"
          [swal]="{ titleText: 'INVOICES.INVOICES_LIST.CREATE_CANCELLATION_TITLE' | translate }"
          (confirm)="delete(dataItem)"
        >
          <i class="fal fa-ban"></i>
        </button>

        <app-sale-button
          *ngIf="dataItem?.invoiceable_type && LaravelModelsEnum.SALE_FINAL && dataItem?.invoiceable?.sale_id"
          [link]="['/sales/view']"
          [params]="{ id: dataItem.invoiceable.sale_id }"
        >
        </app-sale-button>
      </ng-template>
    </kendo-grid-column>

    <ng-template kendoPagerTemplate let-totalPages="totalPages" let-currentPage="currentPage">
      <kendo-pager-prev-buttons></kendo-pager-prev-buttons>
      <kendo-pager-input></kendo-pager-input>
      <kendo-pager-info></kendo-pager-info>
      <kendo-pager-next-buttons></kendo-pager-next-buttons>
      <kendo-pager-page-sizes [pageSizes]="env.kendoGridPageSizes"></kendo-pager-page-sizes>
      <app-kendo-grid-refresh-button (click)="loadData(state)"></app-kendo-grid-refresh-button>
      <button type="button" class="btn kendo-export-btn" [disabled]="grid.loading" kendoGridExcelCommand>
        {{ 'EXPORT_TO_EXCEL' | translate }}
      </button>
    </ng-template>

    <kendo-grid-excel></kendo-grid-excel>
  </kendo-grid>
</div>
